/**
 * Corporate Price List Form Page Component
 * /admin/corporate-price-lists/new and /admin/corporate-price-lists/:id/edit
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiClient from '../api/client';

const CorporatePriceListForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [customers, setCustomers] = useState([]);

  const [formData, setFormData] = useState({
    customer: '',
    name: '',
    effective_date: '',
    expiry_date: '',
    is_active: true,
  });

  useEffect(() => {
    loadCustomers();
    if (isEdit) {
      loadPriceList();
    }
  }, [id]);

  const loadCustomers = async () => {
    try {
      const response = await apiClient.getCustomers({ is_active: true });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setCustomers(data);
    } catch (err) {
      console.error('Failed to load customers:', err);
      setError('Failed to load customers');
    }
  };

  const loadPriceList = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getCorporatePriceList(id);
      
      setFormData({
        customer: data.customer || '',
        name: data.name || '',
        effective_date: data.effective_date ? data.effective_date.split('T')[0] : '',
        expiry_date: data.expiry_date ? data.expiry_date.split('T')[0] : '',
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
    } catch (err) {
      setError(err.message || 'Failed to load price list');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      // Validate dates
      if (formData.effective_date && formData.expiry_date) {
        const effectiveDate = new Date(formData.effective_date);
        const expiryDate = new Date(formData.expiry_date);
        if (expiryDate < effectiveDate) {
          setError('Expiry date must be after or equal to effective date');
          setSaving(false);
          return;
        }
      }

      const submitData = {
        customer: formData.customer,
        name: formData.name,
        is_active: formData.is_active,
      };

      if (formData.effective_date) {
        submitData.effective_date = formData.effective_date;
      }
      if (formData.expiry_date) {
        submitData.expiry_date = formData.expiry_date;
      }

      let response;
      if (isEdit) {
        response = await apiClient.updateCorporatePriceList(id, submitData);
        setSuccessMessage('Price list updated successfully!');
      } else {
        response = await apiClient.createCorporatePriceList(submitData);
        setSuccessMessage('Price list created successfully!');
      }

      // Navigate to price list detail page after a short delay
      setTimeout(() => {
        navigate(`/admin/corporate-price-lists/${response.id || id}`);
      }, 1500);
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} price list`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/corporate-price-lists')}
            sx={{ mb: 1 }}
          >
            Back to Price Lists
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Price List' : 'New Price List'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Customer */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  name="customer"
                  value={formData.customer}
                  onChange={handleChange}
                  label="Customer"
                >
                  <MenuItem value="">Select Customer</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.customer_name || customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {customers.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  No customers available. Please create customers first.
                </Typography>
              )}
            </Grid>

            {/* Name */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                name="name"
                label="Price List Name"
                value={formData.name}
                onChange={handleChange}
                required
                helperText="e.g., Halliburton 2025 Price List"
              />
            </Grid>

            {/* Effective Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="effective_date"
                label="Effective Date"
                type="date"
                value={formData.effective_date}
                onChange={handleChange}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>

            {/* Expiry Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="expiry_date"
                label="Expiry Date"
                type="date"
                value={formData.expiry_date}
                onChange={handleChange}
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="Optional: Leave blank for no expiry"
              />
            </Grid>

            {/* Active Status */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Submit Button */}
            <Grid size={{ xs: 12 }}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/corporate-price-lists')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEdit ? 'Update Price List' : 'Create Price List'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default CorporatePriceListForm;

